import type { UiNode, UiNodeInputAttributes } from '@ory/client';
import { createContext, useContext } from 'react';
import ActionButton from '@/components/Button/ActionButton';
import ResponseMessage from '@/components/Error/ResponseMessage';
import type { IconName } from '@/components/Icon/iconMap';
import { Spacer, VStack } from '@/components/Layout';
import { useNodeTranslation } from '@/features/auth/hooks/useNodeTranslation';
import { defaultNS, useTranslation } from '@/lib/i18n';
type Props = {
  node: UiNode;
  disabled?: boolean;
};

/**
 * 同じ group 内で SendCodeSubmit が複数並ぶケース（候補が複数）を
 * 親 (NodeGroup) から子に伝えるための Context。
 *
 * `isClustered = true` のとき、verb はセクション見出し
 * (`SendCodeSubmitChooseAddressHeading`) で 1 回だけ提示されるため、
 * 各ボタンは icon + address のみのコンパクト表示にする。
 */
const SendCodeSubmitContext = createContext<{
  isClustered: boolean;
}>({
  isClustered: false
});
export const SendCodeSubmitProvider = SendCodeSubmitContext.Provider;

/**
 * UI ノードが SendCodeSubmit が扱う「コード送信先選択」用の submit か判定。
 * NodeGroup でクラスタ検出に使う。
 */
export function isSendCodeAddressNode(node: UiNode): boolean {
  if (node.type !== 'input' || node.group !== 'code') {
    return false;
  }
  const attrs = node.attributes as UiNodeInputAttributes;
  return attrs.type === 'submit' && attrs.name === 'address';
}

/**
 * meta.label.context.address / channel を取り出す。
 * 2FA の "Send code to {address}" ボタン (UI node id 1010023) で
 * 複数候補がある場合に、どのアドレス宛か判別するために使用する。
 */
function extractAddressContext(node: UiNode): {
  address: string;
  channel?: string;
} | undefined {
  const context = node.meta?.label?.context;
  if (!context || typeof context !== 'object' || !('address' in context)) {
    return undefined;
  }
  const address = context.address;
  if (typeof address !== 'string') {
    return undefined;
  }
  const channel = 'channel' in context && typeof context.channel === 'string' ? context.channel : undefined;
  return {
    address,
    channel
  };
}

/** channel に応じてアイコンを選ぶ。未知の channel は Mail にフォールバック。 */
function getChannelIcon(channel: string | undefined): IconName {
  switch (channel) {
    case 'sms':
    case 'phone':
      return 'SmartphoneIcon';
    case 'email':
    default:
      return 'Mail';
  }
}

/**
 * 候補が複数あるときに address ボタン群の上に表示する見出し。
 * verb をここに 1 回だけ集約することで、各ボタンが冗長な
 * 「コードを送信」表記にならないようにする。
 */
export function SendCodeSubmitChooseAddressHeading() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'pages.auth.additionalAuth.sendCodeSubmit'
  });
  return <h2 className="text-sm font-semibold text-neutral-on-fill">
      {t('chooseAddress')}
    </h2>;
}

/**
 * コード送信ボタン
 *
 * 2FA で `group="code"` + `name="address"` の submit ノードに対応する専用 UI。
 * 候補数に応じて 2 形態でレンダリングする:
 *
 * 単独 (isClustered=false):
 *   [icon]  コードを送信                 ← verb (主)
 *           {address}                   ← address (従, muted)
 *
 * 複数候補 (isClustered=true):
 *   [icon]  {address}                   ← address のみ
 *   ※ verb は SendCodeSubmitChooseAddressHeading が見出しとして 1 回表示
 */
export function SendCodeSubmit({
  node,
  disabled
}: Props) {
  const {
    translateUiText
  } = useNodeTranslation();
  const {
    isClustered
  } = useContext(SendCodeSubmitContext);
  const attrs = node.attributes as UiNodeInputAttributes;
  if (attrs.type !== 'submit') {
    return null;
  }
  const ctx = extractAddressContext(node);
  if (!ctx) {
    return null;
  }
  const label = node.meta?.label ? translateUiText(node.meta.label) : undefined;
  const errorMessages = node.messages?.filter(msg => msg.type === 'error').map(msg => translateUiText(msg));
  return <VStack className="w-full gap-0">
      <ActionButton type="submit" name={attrs.name} value={attrs.value as string} disabled={disabled ?? attrs.disabled} variant="default" icon={getChannelIcon(ctx.channel)} aria-label={isClustered && label ? `${label}: ${ctx.address}` : undefined} className="w-full h-auto py-3 justify-start whitespace-normal">
        {isClustered ? <span className="flex-1 min-w-0 text-sm font-semibold break-words text-left">
            {ctx.address}
          </span> : <span className="flex-1 min-w-0 flex flex-col gap-0.5 leading-tight text-left">
            <span className="text-sm font-semibold">{label}</span>
            <span className="text-sm font-normal text-primary-muted-on-fill break-words">
              {ctx.address}
            </span>
          </span>}
      </ActionButton>
      {errorMessages && errorMessages.length > 0 && <>
          <Spacer variant="xxs" />
          <ResponseMessage type="error" size="xs">
            {errorMessages[0]}
          </ResponseMessage>
        </>}
    </VStack>;
}